import { createContext, useReducer } from "react";
import { produce } from "immer";
import type { Url } from "./types";
import { type ConfigId } from "@/constants/ConfigData";

interface State {
  botData: Record<string, any>;
  openConfigModalId: ConfigId | "";
  crawlData: Map<string, Url>;
  extraData: Map<string, Url>;
  url: string;
  tab: number;
  callGenaiFnc?: string[];
  config?: string[];
  textAreaContent: null | string;
  textAreaContentPrev: null | string;
  currentToggle?: number;
  characterCount?: number | null;
  isChannel?: boolean;
  curentCustomModelData?: any;
  textAreaStatus?: any;
}

type Action =
  | { type: "set-botData"; botData: Record<string, any> }
  | { type: "set-characterCount"; characterCount: number }
  | { type: "set-isChannel"; isChannel: boolean }
  | { type: "set-updatedAt"; updatedAt: string }
  | { type: "set-openConfigModalId"; configId: ConfigId | "" }
  | { type: "tab-set"; tab: number }
  | { type: "url-set"; url: string }
  | { type: "set-config"; config: string[] }
  | { type: "set-currentToggle"; currentToggle: number }
  | {
      type: "crawl-init";
      crawlData: Map<string, Url>;
    }
  | { type: "crawl-add"; crawlData: Map<string, Url> }
  | { type: "crawl-remove"; ids: string[] }
  | { type: "crawl-toggle"; id: string }
  | { type: "crawl-set-selected"; range: number; state: boolean }
  | { type: "extra-add"; extraData: Map<string, Url>; selected: boolean }
  | { type: "extra-select"; id: string }
  | { type: "extra-delete"; id: string }
  | { type: "extra-delete-all" }
  | { type: "data-sources-set"; dataSources: Record<string, any> }
  | { type: "data-sources-update"; dataSources: Record<string, any> }
  | { type: "toggle-genai-function"; functionNames: string[] }
  | { type: "crawl-delete"; id: string }
  | {
      type: "set-aiSource";
      dataKey: string;
      dataSet: any[];
      characterUsed?: number;
    }
  | { type: "add-aiSource"; dataKey: string; dataSet: any[] }
  | { type: "set-textAreaContent"; textAreaContent: string | null }
  | { type: "set-textAreaContentPrev"; textAreaContent: string | null }
  | { type: "set-textAreaStatus"; textAreaStatus: boolean } // <-- Re-added
  | {
      type: "set-currentCustomModelData";
      curentCustomModelData: Record<any, any>;
    }
  | { type: "update-aiSource-url"; urlData: any[] };

type Context = [State, React.Dispatch<Action>];

const initialState: State = {
  botData: {},
  openConfigModalId: "",
  config: [],
  url: "",
  // tab: window.screen.width <= 1279 ? 2 : 1,
  tab: 1,
  crawlData: new Map(),
  extraData: new Map(),
  textAreaContent: null,
  textAreaContentPrev: null,
  characterCount: null,
  isChannel: false,
  curentCustomModelData: {},
  textAreaStatus: null,
};

const reducer = produce((draft: State, action: Action) => {
  switch (action.type) {
    case "set-botData": {
      draft.botData = {
        // ...draft.botData,
        ...action.botData,
      };
      break;
    }
    case "set-updatedAt": {
      draft.botData.updatedAt = action.updatedAt;
      break;
    }
    case "set-openConfigModalId": {
      draft.openConfigModalId = action.configId;
      break;
    }
    case "set-config": {
      draft.config = action.config;
      break;
    }
    case "tab-set": {
      draft.tab = action.tab;
      break;
    }
    case "set-currentToggle": {
      draft.currentToggle = action.currentToggle;
      break;
    }
    case "set-characterCount": {
      draft.characterCount = action.characterCount;
      break;
    }
    case "set-isChannel": {
      draft.isChannel = action.isChannel;
      break;
    }
    case "url-set": {
      draft.url = action.url;
      break;
    }
    case "crawl-init": {
      draft.crawlData = action.crawlData;
      break;
    }
    case "crawl-add": {
      action.crawlData.forEach((value, key) => {
        draft.crawlData.set(key, value);
      });
      break;
    }
    case "crawl-remove": {
      for (const id of action.ids) {
        draft.crawlData.delete(id);
      }
      break;
    }
    case "crawl-toggle": {
      const value = draft.crawlData.get(action.id);
      if (!value) break;
      draft.crawlData.set(action.id, { ...value, selected: !value.selected });
      break;
    }
    case "crawl-set-selected": {
      let counter = 0;
      draft.crawlData.forEach((value, key) => {
        if (action.range === -1 || counter < action.range) {
          draft.crawlData.set(key, { ...value, selected: action.state });
        } else {
          draft.crawlData.set(key, { ...value, selected: !action.state });
        }
        counter++;
      });
      break;
    }
    case "extra-add": {
      const newEntries = new Map();
      action.extraData.forEach((value, key) => {
        newEntries.set(key, { ...value });
      });
      draft.crawlData.forEach((value, key) => {
        if (!newEntries.has(key)) {
          newEntries.set(key, value);
        }
      });
      draft.crawlData = newEntries;
      break;
    }
    case "extra-select": {
      const value = draft.extraData.get(action.id);
      if (!value) break;
      draft.extraData.set(action.id, { ...value, selected: true });
      break;
    }
    case "extra-delete": {
      draft.extraData.delete(action.id);
      break;
    }
    case "extra-delete-all": {
      draft.extraData = new Map();
      break;
    }
    case "data-sources-set": {
      draft.botData.dataSources = action.dataSources;
      break;
    }
    case "data-sources-update": {
      draft.botData.dataSources = {
        ...draft.botData.dataSources,
        ...action.dataSources,
      };
      break;
    }
    case "set-aiSource": {
      if (!draft.botData.aiSource) {
        draft.botData.aiSource = {};
      }
      draft.botData.aiSource[action.dataKey] = action.dataSet;
      if (action.characterUsed !== undefined) {
        if (!draft.botData.aiBotConfig) {
          draft.botData.aiBotConfig = {};
        }
        // Always preserve the character count, even if it's 0
        draft.botData.aiBotConfig.characterUsed = action.characterUsed;
        // Also update the context store's characterCount for persistence across tabs
        draft.characterCount = action.characterUsed;
      }
      break;
    }
    case "add-aiSource": {
      if (!draft.botData.aiSource) {
        draft.botData.aiSource = {};
      }
      draft.botData.aiSource[action.dataKey] = [
        ...(draft.botData.aiSource[action.dataKey] || []),
        ...action.dataSet,
      ];
      break;
    }
    case "toggle-genai-function": {
      draft.callGenaiFnc = action.functionNames;
      break;
    }
    case "crawl-delete": {
      draft.crawlData.delete(action.id);
      break;
    }
    case "set-textAreaContent": {
      draft.textAreaContent = action.textAreaContent;
      break;
    }
    case "set-textAreaContentPrev": {
      draft.textAreaContentPrev = action.textAreaContent;
      break;
    }
    case "set-textAreaStatus": {
      draft.textAreaStatus = action.textAreaStatus;
      break;
    }
    case "set-currentCustomModelData": {
      draft.curentCustomModelData = action.curentCustomModelData;
      break;
    }
    case "update-aiSource-url": {
      if (!draft.botData.aiSource) {
        draft.botData.aiSource = {};
      }

      const urlDataObj = {};
      for (const item of action.urlData) {
        urlDataObj[item.url] = item;
      }

      draft.botData.aiSource.urls = draft.botData.aiSource.urls.map((item) => {
        if (item.url in urlDataObj) {
          return urlDataObj[item.url];
        }

        return item;
      });

      break;
    }
  }
});

export const BotContext = createContext<Context>([initialState, () => {}]);

export default function BotContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [store, storeDispatch] = useReducer(reducer, initialState);

  return (
    <BotContext.Provider value={[store, storeDispatch]}>
      {children}
    </BotContext.Provider>
  );
}
